import { useId } from "react";
import { Check, ExternalLink } from "lucide-react";
import froppioBusiness from "@/assets/froppio-business.svg";
import { Button } from "@/shared/components/shadcn/Button";
import { Card } from "@/shared/components/ui/Card";
import { SectionHeader } from "@/shared/components/ui/SectionHeader";
import { StatusBadge, type StatusBadgeProps } from "@/shared/components/ui/StatusBadge";
import { cn } from "@/shared/utils/cn";
import type { StripeConnection } from "../types/stripeTypes";

const states = {
  not_connected: {
    label: "Not connected",
    title: "Connect your business",
    description: "Continue to Stripe to set up your business account securely in your browser.",
    tone: "neutral",
  },
  setup_incomplete: {
    label: "Setup incomplete",
    title: "Finish your Stripe setup",
    description: "Continue the hosted setup for your existing account. You do not need to create another account.",
    tone: "pending",
  },
  verification_pending: {
    label: "Verification pending",
    title: "Stripe is reviewing your account",
    description: "Stripe is reviewing your information. Verification is not complete yet. Refresh later to check for updates.",
    tone: "pending",
  },
  connected: {
    label: "Connected",
    title: "Stripe business account",
    description: "Connected across all your business locations.",
    tone: "positive",
  },
  action_required: {
    label: "Action required",
    title: "Update your Stripe information",
    description: "Stripe needs more information. Continue setup for your existing account.",
    tone: "pending",
  },
  connection_removed: {
    label: "Connection removed",
    title: "Your Stripe connection was removed",
    description: "Contact Froppio support to recover this connection. Do not create a replacement account.",
    tone: "danger",
  },
} satisfies Record<StripeConnection["status"], {
  label: string;
  title: string;
  description: string;
  tone: StatusBadgeProps["tone"];
}>;

interface StripeConnectionCardProps {
  connection: StripeConnection;
  businessName: string;
  busy: boolean;
  onConnect: () => void;
}

export function StripeConnectionCard({ connection, businessName, busy, onConnect }: StripeConnectionCardProps) {
  const headingId = useId();
  const state = states[connection.status];
  const notConnected = connection.status === "not_connected";
  const connected = connection.status === "connected";
  const canConnect = notConnected || connection.status === "setup_incomplete" || connection.status === "action_required";
  const canOpenDashboard = connection.account !== null && connection.status !== "connection_removed";
  const title = connected ? connection.account?.businessName?.trim() || businessName : state.title;
  const actions = (
    <div className="flex flex-col gap-2 sm:shrink-0 sm:flex-row sm:flex-wrap">
      {canConnect && (
        <Button disabled={busy} onClick={onConnect}>
          {busy ? "Opening Stripe..." : notConnected ? "Connect Stripe" : "Continue setup"}
        </Button>
      )}
      {canOpenDashboard && (
        <Button variant={connected ? "default" : "outline"} asChild>
          <a href="https://dashboard.stripe.com/" target="_blank" rel="noopener noreferrer">
            Open Stripe Dashboard
            <ExternalLink aria-hidden="true" />
          </a>
        </Button>
      )}
    </div>
  );

  return (
    <Card tone={notConnected || connected ? "matcha" : "default"} role="region" aria-labelledby={headingId}>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <span className="text-[26px] font-extrabold tracking-tight text-primary">stripe</span>
        <div className="flex flex-wrap items-center gap-2">
          {connection.environment === "test" && <StatusBadge tone="pending">Test mode</StatusBadge>}
          <StatusBadge tone={state.tone} className={cn("gap-1", (notConnected || connected) && "bg-card/80")}>
            {connected && <Check className="size-3.5" aria-hidden="true" />}
            {state.label}
          </StatusBadge>
        </div>
      </div>
      <div className={cn(
        notConnected
          ? "grid items-center gap-3 xs:grid-cols-[minmax(0,1fr)_90px] sm:grid-cols-[minmax(0,1fr)_150px] sm:gap-7"
          : "flex flex-col gap-5 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between",
      )}>
        <div className="min-w-0 space-y-3 sm:max-w-[480px]">
          <SectionHeader title={title} headingId={headingId} />
          <p className="text-sm leading-relaxed">{state.description}</p>
          {notConnected && <div className="pt-2">{actions}</div>}
        </div>
        {notConnected ? (
          <img src={froppioBusiness} alt="" width={280} height={240} className="hidden h-auto w-full xs:block" />
        ) : actions}
      </div>
    </Card>
  );
}
